/* eslint-disable */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, ImageOff, Maximize2, FileBarChart } from "lucide-react";
import api from "@/lib/api";
import {
  Panel, StatusPill, EmptyState, Loading, SectionLabel, HEALTH_LABELS,
} from "@/components/app/primitives";
import { OrgChart } from "@/components/app/OrgChart";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { moneyCompact, months as monthsFmt, ratioLabel, EMPTY_MARK } from "@/lib/format";

function initials(name = "") {
  return name.split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

function StructurePanel({ assetId }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(!!assetId);
  const [full, setFull] = useState(false);

  useEffect(() => {
    let active = true;
    if (!assetId) { setUrl(null); setLoading(false); return; }
    setLoading(true);
    api.get(`/mission-control/structure-image/${assetId}`)
      .then((res) => { if (active) setUrl(res.data.data_url); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [assetId]);

  return (
    <Panel
      title="Company structure"
      testId="mission-control-structure-panel"
      bodyClassName="p-4"
      action={url && (
        <button data-testid="mission-control-structure-image-fullscreen" onClick={() => setFull(true)} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <Maximize2 className="h-3.5 w-3.5" strokeWidth={1.75} /> Full screen
        </button>
      )}
    >
      {loading ? (
        <Loading label="Loading structure" />
      ) : url ? (
        <div className="overflow-hidden rounded-md border border-border bg-background/40">
          <AspectRatio ratio={16 / 9}>
            <img data-testid="mission-control-structure-image" src={url} alt="Company structure" className="h-full w-full object-contain" />
          </AspectRatio>
        </div>
      ) : (
        <div data-testid="mission-control-structure-image-empty" className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-background/30 py-14 text-center">
          <ImageOff className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground">No structure image uploaded.</p>
          <p className="text-xs text-muted-foreground">An Owner or Founder can add one in the Admin console.</p>
        </div>
      )}
      <Dialog open={full} onOpenChange={setFull}>
        <DialogContent className="max-w-5xl">
          <DialogHeader><DialogTitle className="font-display text-lg">Company structure</DialogTitle></DialogHeader>
          {url && <img src={url} alt="Company structure" className="w-full rounded-md border border-border" />}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

function VentureCard({ v, onOpen }) {
  return (
    <button
      type="button"
      data-testid="mission-control-venture-card"
      onClick={() => onOpen(v.id)}
      className="group text-left rounded-md border border-border bg-card p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] hover:border-primary/40 transition-colors duration-150"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-display text-lg text-foreground leading-none truncate">{v.name}</p>
          <p className="mt-1 text-xs text-muted-foreground truncate">{v.arm} <span className="text-muted-foreground/50">/</span> {v.stage}</p>
        </div>
        <StatusPill tone={v.health} label={HEALTH_LABELS[v.health] || v.health} />
      </div>
      {v.restricted ? (
        <p className="mt-3 text-xs text-muted-foreground">Metrics restricted to the studio team.</p>
      ) : v.has_metrics ? (
        <div className="mt-3 grid grid-cols-3 gap-2">
          <div><SectionLabel>Runway</SectionLabel><p className="mt-0.5 font-mono tabular-nums text-sm text-foreground">{monthsFmt(v.runway_months)}</p></div>
          <div><SectionLabel>Revenue</SectionLabel><p className="mt-0.5 font-mono tabular-nums text-sm text-foreground">{moneyCompact(v.revenue)}</p></div>
          <div><SectionLabel>LTV:CAC</SectionLabel><p className="mt-0.5 font-mono tabular-nums text-sm text-foreground">{ratioLabel(v.ltv_cac_ratio)}</p></div>
        </div>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground">No metrics recorded yet.</p>
      )}
      <div className="mt-3 flex items-center justify-end border-t border-border pt-2 text-xs text-muted-foreground group-hover:text-foreground transition-colors">
        Open command center <ArrowUpRight className="h-3.5 w-3.5 ml-1" strokeWidth={1.75} />
      </div>
    </button>
  );
}

export default function MissionControl() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/mission-control/overview");
      setData(res.data);
    } catch (e) {
      toast.error("Could not load Mission Control.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loading label="Loading Mission Control" />;
  if (!data) return <EmptyState message="Nothing to show yet." />;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <SectionLabel>Studio overview</SectionLabel>
        <Button data-testid="mission-control-open-weekly-digest" variant="outline" onClick={() => navigate("/mission-control/digest")} className="h-9 gap-2">
          <FileBarChart className="h-4 w-4" strokeWidth={1.75} /> Weekly digest
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 xl:col-span-7"><StructurePanel assetId={data.structure_image?.asset_id} /></div>
        <div className="col-span-12 xl:col-span-5">
          <Panel title="Organisation" testId="mission-control-org-panel" bodyClassName="p-4">
            <OrgChart roots={data.org?.roots || []} />
          </Panel>
        </div>
      </div>

      <Panel title="Teams" testId="mission-control-teams-panel" bodyClassName="p-4">
        {data.teams.length === 0 ? (
          <EmptyState message="No teams yet." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {data.teams.map((t) => (
              <div key={t.id} data-testid="mission-control-team-card" className="rounded-md border border-border bg-card p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm text-foreground truncate">{t.name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{t.region || EMPTY_MARK}</p>
                  </div>
                  <span className="font-mono tabular-nums text-xs text-muted-foreground">{t.member_count}</span>
                </div>
                <div className="mt-3 space-y-1.5">
                  {t.members.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No members</p>
                  ) : (
                    t.members.map((m) => (
                      <div key={m.id} className="flex items-center gap-2">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[rgba(43,79,216,0.18)] text-[9px] font-semibold text-[#DCE6FF]">{initials(m.name)}</span>
                        <span className="truncate text-xs text-foreground">{m.name}</span>
                        {t.lead_name === m.name && <span className="text-[9px] uppercase tracking-[0.1em] text-muted-foreground">Lead</span>}
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Ventures" testId="mission-control-ventures-panel" bodyClassName="p-4">
        {data.ventures.length === 0 ? (
          <EmptyState message="No ventures are visible to you." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {data.ventures.map((v) => (
              <VentureCard key={v.id} v={v} onOpen={(id) => navigate(`/mission-control/venture/${id}`)} />
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
